'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import fr from '@/locales/fr.json';
import ar from '@/locales/ar.json';

export type Language = 'fr' | 'ar';

export const SUPPORTED_LANGUAGES: { code: Language; label: string; flag: string; dir: 'ltr' | 'rtl' }[] = [
  { code: 'fr', label: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'ar', label: 'العربية', flag: '🇹🇳', dir: 'rtl' },
];

const translations: Record<Language, typeof fr> = { fr, ar: ar as typeof fr };

export function getTranslations(language: Language): typeof fr {
  return translations[language] ?? translations.fr;
}

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: typeof fr;
  dir: 'ltr' | 'rtl';
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: 'fr',
  setLanguage: () => {},
  t: fr,
  dir: 'ltr',
  isRTL: false,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>('fr');

  useEffect(() => {
    try {
      const saved = localStorage.getItem('dalili_lang') as Language | null;
      if (saved && (saved === 'fr' || saved === 'ar')) {
        setLanguageState(saved);
      }
    } catch {}
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem('dalili_lang', lang);
    } catch {}
  };

  const langConfig = SUPPORTED_LANGUAGES.find((l) => l.code === language)!;
  const dir = langConfig.dir;
  const isRTL = dir === 'rtl';
  const t = getTranslations(language);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, dir, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
